import request from '@/config/request.js'
import common from '@/components/common/'

const ApiHome = request.Home

const MONTHS_IN_TERM = 4.5

function createMeasureForm () {
  return {
    monthlyFixedCost: null,
    perStudentMonthlyVariableCost: null,
    avgMonthlyPrice: null,
    currentStudentNum: null
  }
}

function getPageContent (res) {
  if (res && Array.isArray(res.content)) return res.content
  if (res && res.data && Array.isArray(res.data.content)) return res.data.content
  if (res && Array.isArray(res.data)) return res.data
  return []
}

function getResponseData (res) {
  if (res && res.assess) return res
  if (res && res.data && res.data.assess) return res.data
  return {}
}

function toNum (value) {
  if (value === '' || value === null || value === undefined) return 0
  const num = Number(value)
  return Number.isNaN(num) ? 0 : num
}

function getCostAmount (costs, code) {
  const list = Array.isArray(costs) ? costs : []
  for (let i = 0; i < list.length; i++) {
    const item = list[i]
    if (item && item.costCode === code) {
      return toNum(item.amount)
    }
  }
  return 0
}

export default {
  name: 'campus-measure',
  components: {
    ...common
  },
  data () {
    return {
      loading: false,
      monthsInTerm: MONTHS_IN_TERM,
      semesterOptions: [],
      semesterId: '',
      measureForm: createMeasureForm(),
      sourceAssess: {}
    }
  },
  computed: {
    marginContribution () {
      const price = toNum(this.measureForm.avgMonthlyPrice)
      const variableCost = toNum(this.measureForm.perStudentMonthlyVariableCost)
      const result = price - variableCost
      return result > 0 ? result : 0
    },
    breakEvenStudents () {
      const fixedCost = toNum(this.measureForm.monthlyFixedCost)
      const contribution = this.marginContribution
      if (fixedCost <= 0 || contribution <= 0) return 0
      return Math.ceil(fixedCost / contribution)
    },
    breakEvenRevenue () {
      const students = this.breakEvenStudents
      const price = toNum(this.measureForm.avgMonthlyPrice)
      if (students <= 0 || price <= 0) return 0
      return students * price
    },
    exceedCount () {
      const current = Math.floor(toNum(this.measureForm.currentStudentNum))
      const target = this.breakEvenStudents
      if (target <= 0) return 0
      return current - target
    },
    progressPercent () {
      const target = this.breakEvenStudents
      const current = Math.floor(toNum(this.measureForm.currentStudentNum))
      if (target <= 0) return 0
      if (current >= target) return 100
      return (current / target) * 100
    },
    progressText () {
      const current = Math.floor(toNum(this.measureForm.currentStudentNum))
      const target = this.breakEvenStudents
      return '当前 ' + current + ' 人 / 平衡点 ' + target + ' 人'
    },
    exceedText () {
      const num = this.exceedCount
      if (this.breakEvenStudents <= 0) return ''
      if (num >= 0) return '超出平衡点 ' + num + ' 人'
      return '低于平衡点 ' + Math.abs(num) + ' 人'
    },
    isExceed () {
      return this.exceedCount >= 0
    }
  },
  created () {
    this.initSemesters()
  },
  methods: {
    formatNumber (value, precision) {
      const num = Number(value)
      if (Number.isNaN(num)) return '--'
      const fixed = num.toFixed(typeof precision === 'number' ? precision : 2)
      return fixed.replace(/\.?0+$/, '')
    },
    formatCurrency (value) {
      return '¥' + this.formatNumber(value, 2)
    },
    buildSemesterLabel (item) {
      const schoolYear = item && item.schoolYear ? String(item.schoolYear) : ''
      const term = item && item.term ? String(item.term) : ''
      const zoneName = item && item.zoneName ? String(item.zoneName) : ''
      const head = (schoolYear || '--') + (term ? (' ' + term) : '')
      return zoneName ? (head + ' — ' + zoneName) : head
    },
    initSemesters () {
      this.loading = true
      ApiHome.zoneHealthPage.call(this, { page: 0, size: 500, data: {} }, res => {
        const list = getPageContent(res)
        this.semesterOptions = Array.isArray(list)
          ? list.map(item => {
            return {
              id: item && item.id ? item.id : '',
              label: this.buildSemesterLabel(item),
              raw: item
            }
          }).filter(item => item.id)
          : []
        if (this.semesterOptions.length) {
          this.semesterId = this.semesterOptions[0].id
          this.loadDetail(this.semesterId)
        } else {
          this.semesterId = ''
          this.sourceAssess = {}
          this.measureForm = createMeasureForm()
        }
        this.loading = false
      }, () => {
        this.loading = false
      })
    },
    onSemesterChange (id) {
      if (!id) return
      this.loadDetail(id)
    },
    loadDetail (id) {
      if (!id) return
      this.loading = true
      ApiHome.zoneHealthGetById.call(this, { id: id }, res => {
        const detail = getResponseData(res)
        const assess = detail && detail.assess ? detail.assess : {}
        const costs = detail && Array.isArray(detail.costs) ? detail.costs : []
        this.sourceAssess = Object.assign({}, assess)
        this.applyAssessToForm(assess, costs)
        this.loading = false
      }, () => {
        this.loading = false
      })
    },
    applyAssessToForm (assess, costs) {
      const months = this.monthsInTerm
      const studentNum = toNum(assess && assess.studentNum)
      const fixedSemester = getCostAmount(costs, 'RENT_PROPERTY') +
        getCostAmount(costs, 'SALARY_FEE') +
        getCostAmount(costs, 'SOCIAL_FEE') +
        getCostAmount(costs, 'WATER_FEE') +
        getCostAmount(costs, 'POWER_FEE')
      const variableSemester = getCostAmount(costs, 'MEAL_FEE') +
        getCostAmount(costs, 'SNACK_FEE')
      const confirmIncome = toNum(assess && assess.confirmIncome)

      const monthlyFixedCost = months > 0 ? (fixedSemester / months) : 0
      const perStudentMonthlyVariableCost = (months > 0 && studentNum > 0)
        ? (variableSemester / studentNum / months)
        : 0
      const avgMonthlyPrice = (months > 0 && studentNum > 0)
        ? (confirmIncome / studentNum / months)
        : 0

      this.measureForm = Object.assign(createMeasureForm(), {
        monthlyFixedCost: monthlyFixedCost,
        perStudentMonthlyVariableCost: perStudentMonthlyVariableCost,
        avgMonthlyPrice: avgMonthlyPrice,
        currentStudentNum: studentNum
      })
    }
  }
}
